import * as THREE from 'three';

const patchedKey = '__phdsoftThreePatched';

// Initialize patched state on window to avoid TypeScript import mutation error
declare global {
  interface Window {
    __phdsoftThreePatched?: boolean;
  }
}

if (!(window as any).__phdsoftThreePatched) {
  (window as any).__phdsoftThreePatched = true;

  const {
    BackSide,
    FrontSide,
    Matrix4,
    Mesh,
    Ray,
    Raycaster,
    Sphere,
    Triangle,
    Vector2,
    Vector3,
  } = THREE;

  (Raycaster.prototype as any).tolerance = 0;

  const ascSort = (a: any, b: any) => a.distance - b.distance;

  const getScale = (object: any) => {
    const scaleValue = object?.userData?.scale;
    return Number.isFinite(scaleValue) ? scaleValue : 1;
  };

  const getTolerance = (object: any) => {
    const toleranceValue = object?.userData?.tolerance;
    return Number.isFinite(toleranceValue) ? toleranceValue : 0;
  };

  const intersectObject = (
    object: any,
    raycaster: any,
    intersects: any[],
    recursive: boolean,
  ) => {
    if (
      object.visible &&
      (object.type === 'Mesh' ||
        object.type === 'Group' ||
        object.type === 'Sprite')
    ) {
      object.userData.tolerance = Number.isFinite(raycaster.tolerance)
        ? raycaster.tolerance
        : 0;

      if (object.layers.test(raycaster.layers)) {
        object.raycast(raycaster, intersects);
      }

      if (recursive === true) {
        const children = object.children;

        for (let i = 0, l = children.length; i < l; i++) {
          intersectObject(children[i], raycaster, intersects, true);
        }
      }
    }
  };

  (Raycaster.prototype as any).intersectObject = function (
    object: any,
    recursive = true,
    intersects: any[] = [],
  ) {
    intersectObject(object, this, intersects, recursive);

    intersects.sort(ascSort);

    return intersects;
  };

  (Raycaster.prototype as any).intersectObjects = function (
    objects: any[],
    recursive = true,
    intersects: any[] = [],
  ) {
    if (Array.isArray(objects) === false) {
      console.warn('THREE.Raycaster.intersectObjects: objects is not an Array.');
      return intersects;
    }

    for (let i = 0, l = objects.length; i < l; i++) {
      intersectObject(objects[i], this, intersects, recursive);
    }

    intersects.sort(ascSort);

    return intersects;
  };

  const _edge1 = new Vector3();
  const _edge2 = new Vector3();
  const _normal = new Vector3();
  const _diff = new Vector3();

  (Ray.prototype as any).intersectTriangle = function (
    a: THREE.Vector3,
    b: THREE.Vector3,
    c: THREE.Vector3,
    backfaceCulling: boolean,
    target: THREE.Vector3,
    tolerance = 0,
  ) {
    if (!Number.isFinite(tolerance)) {
      tolerance = 0;
    }

    _edge1.subVectors(b, a);
    _edge2.subVectors(c, a);
    _normal.crossVectors(_edge1, _edge2);

    let DdN = this.direction.dot(_normal);
    let sign;

    if (DdN > 0) {
      if (backfaceCulling) return null;
      sign = 1;
    } else if (DdN < 0) {
      sign = -1;
      DdN = -DdN;
    } else {
      return null;
    }

    _diff.subVectors(this.origin, a);
    const DdQxE2 = sign * this.direction.dot(_edge2.crossVectors(_diff, _edge2));

    if (DdQxE2 + tolerance < 0) {
      return null;
    }

    const DdE1xQ = sign * this.direction.dot(_edge1.cross(_diff));

    if (DdE1xQ + tolerance < 0) {
      return null;
    }

    if (DdQxE2 + DdE1xQ > DdN + tolerance) {
      return null;
    }

    const QdN = -sign * _diff.dot(_normal);

    if (QdN + tolerance < 0) {
      return null;
    }

    return this.at(QdN / DdN, target);
  };

  const _inverseMatrix = new Matrix4();
  const _ray = new Ray();
  const _sphere = new Sphere();
  const _sphereHitAt = new Vector3();

  const _vA = new Vector3();
  const _vB = new Vector3();
  const _vC = new Vector3();

  const _tempA = new Vector3();
  const _morphA = new Vector3();

  const _intersectionPoint = new Vector3();
  const _intersectionPointWorld = new Vector3();

  const checkIntersection = (
    object: any,
    material: any,
    raycaster: any,
    ray: any,
    pA: THREE.Vector3,
    pB: THREE.Vector3,
    pC: THREE.Vector3,
    point: THREE.Vector3,
  ) => {
    let intersect;
    const scale = getScale(object);
    const tolerance = scale === 1 ? 0 : getTolerance(object);

    if (material.side === BackSide) {
      intersect = ray.intersectTriangle(pC, pB, pA, true, point, tolerance);
    } else {
      intersect = ray.intersectTriangle(
        pA,
        pB,
        pC,
        material.side === FrontSide,
        point,
        tolerance,
      );
    }

    if (intersect === null) return null;

    _intersectionPointWorld.copy(point);
    _intersectionPointWorld.applyMatrix4(object.matrixWorld);

    const distance = raycaster.ray.origin.distanceTo(_intersectionPointWorld);

    if (distance < raycaster.near || distance > raycaster.far) return null;

    return {
      distance: distance,
      point: _intersectionPointWorld.clone(),
      object: object,
    };
  };

  const checkGeometryIntersection = (
    object: any,
    material: any,
    raycaster: any,
    ray: any,
    uv: any,
    uv1: any,
    normal: any,
    a: number,
    b: number,
    c: number,
  ) => {
    object.getVertexPosition(a, _vA);
    object.getVertexPosition(b, _vB);
    object.getVertexPosition(c, _vC);

    const intersection: any = checkIntersection(
      object,
      material,
      raycaster,
      ray,
      _vA,
      _vB,
      _vC,
      _intersectionPoint,
    );

    if (intersection) {
      const barycoord = new Vector3();
      Triangle.getBarycoord(_intersectionPoint, _vA, _vB, _vC, barycoord);

      if (uv) {
        intersection.uv = Triangle.getInterpolatedAttribute(
          uv,
          a,
          b,
          c,
          barycoord,
          new Vector2(),
        );
      }

      if (uv1) {
        intersection.uv1 = Triangle.getInterpolatedAttribute(
          uv1,
          a,
          b,
          c,
          barycoord,
          new Vector2(),
        );
      }

      if (normal) {
        intersection.normal = Triangle.getInterpolatedAttribute(
          normal,
          a,
          b,
          c,
          barycoord,
          new Vector3(),
        );

        if (intersection.normal.dot(ray.direction) > 0) {
          intersection.normal.multiplyScalar(-1);
        }
      }

      const face = {
        a: a,
        b: b,
        c: c,
        normal: new Vector3(),
        materialIndex: 0,
      };

      Triangle.getNormal(_vA, _vB, _vC, face.normal);

      intersection.face = face;
      intersection.barycoord = barycoord;
    }

    return intersection;
  };

  const meshPrototype = Mesh.prototype as any;

  meshPrototype.raycast = function (raycaster: any, intersects: any[]) {
    const geometry = this.geometry;
    const material = this.material;
    const matrixWorld = this.matrixWorld;

    if (material === undefined) return;

    const scale = getScale(this);

    if (geometry.boundingSphere === null) geometry.computeBoundingSphere();

    _sphere.copy(geometry.boundingSphere);
    _sphere.radius *= scale;
    _sphere.applyMatrix4(matrixWorld);

    _ray.copy(raycaster.ray).recast(raycaster.near);

    if (_sphere.containsPoint(_ray.origin) === false) {
      if (_ray.intersectSphere(_sphere, _sphereHitAt) === null) return;

      if (
        _ray.origin.distanceToSquared(_sphereHitAt) >
        (raycaster.far - raycaster.near) ** 2
      )
        return;
    }

    _inverseMatrix.copy(matrixWorld).invert();
    _ray.copy(raycaster.ray).applyMatrix4(_inverseMatrix);

    if (geometry.boundingBox !== null) {
      const scaledBox = geometry.boundingBox.clone().expandByScalar(scale);
      if (_ray.intersectsBox(scaledBox) === false) return;
    }

    this._computeIntersections(raycaster, intersects, _ray);
  };

  meshPrototype._computeIntersections = function (
    raycaster: any,
    intersects: any[],
    rayLocalSpace: any,
  ) {
    let intersection;

    const geometry = this.geometry;
    const material = this.material;

    const index = geometry.index;
    const position = geometry.attributes.position;
    const uv = geometry.attributes.uv;
    const uv1 = geometry.attributes.uv1;
    const normal = geometry.attributes.normal;
    const groups = geometry.groups;
    const drawRange = geometry.drawRange;

    if (index !== null) {
      if (Array.isArray(material)) {
        for (let i = 0, il = groups.length; i < il; i++) {
          const group = groups[i];
          const groupMaterial = material[group.materialIndex];

          const start = Math.max(group.start, drawRange.start);
          const end = Math.min(
            index.count,
            Math.min(group.start + group.count, drawRange.start + drawRange.count),
          );

          for (let j = start, jl = end; j < jl; j += 3) {
            const a = index.getX(j);
            const b = index.getX(j + 1);
            const c = index.getX(j + 2);

            intersection = checkGeometryIntersection(
              this,
              groupMaterial,
              raycaster,
              rayLocalSpace,
              uv,
              uv1,
              normal,
              a,
              b,
              c,
            );

            if (intersection) {
              intersection.faceIndex = Math.floor(j / 3);
              intersection.face.materialIndex = group.materialIndex;
              intersects.push(intersection);
            }
          }
        }
      } else {
        const start = Math.max(0, drawRange.start);
        const end = Math.min(index.count, drawRange.start + drawRange.count);

        for (let i = start, il = end; i < il; i += 3) {
          const a = index.getX(i);
          const b = index.getX(i + 1);
          const c = index.getX(i + 2);

          intersection = checkGeometryIntersection(
            this,
            material,
            raycaster,
            rayLocalSpace,
            uv,
            uv1,
            normal,
            a,
            b,
            c,
          );

          if (intersection) {
            intersection.faceIndex = Math.floor(i / 3);
            intersects.push(intersection);
          }
        }
      }
    } else if (position !== undefined) {
      if (Array.isArray(material)) {
        for (let i = 0, il = groups.length; i < il; i++) {
          const group = groups[i];
          const groupMaterial = material[group.materialIndex];

          const start = Math.max(group.start, drawRange.start);
          const end = Math.min(
            position.count,
            Math.min(group.start + group.count, drawRange.start + drawRange.count),
          );

          for (let j = start, jl = end; j < jl; j += 3) {
            const a = j;
            const b = j + 1;
            const c = j + 2;

            intersection = checkGeometryIntersection(
              this,
              groupMaterial,
              raycaster,
              rayLocalSpace,
              uv,
              uv1,
              normal,
              a,
              b,
              c,
            );

            if (intersection) {
              intersection.faceIndex = Math.floor(j / 3);
              intersection.face.materialIndex = group.materialIndex;
              intersects.push(intersection);
            }
          }
        }
      } else {
        const start = Math.max(0, drawRange.start);
        const end = Math.min(position.count, drawRange.start + drawRange.count);

        for (let i = start, il = end; i < il; i += 3) {
          const a = i;
          const b = i + 1;
          const c = i + 2;

          intersection = checkGeometryIntersection(
            this,
            material,
            raycaster,
            rayLocalSpace,
            uv,
            uv1,
            normal,
            a,
            b,
            c,
          );

          if (intersection) {
            intersection.faceIndex = Math.floor(i / 3);
            intersects.push(intersection);
          }
        }
      }
    }
  };
}

export * from 'three';

// Re-export deprecated Geometry classes from local shim
export { Geometry, Face3 } from './Geometry.js';

// Deprecated aliases for backwards compatibility
export const BoxBufferGeometry = THREE.BoxGeometry;
export const CylinderBufferGeometry = THREE.CylinderGeometry;
export const PlaneBufferGeometry = THREE.PlaneGeometry;
export const RingBufferGeometry = THREE.RingGeometry;
export const ShapeBufferGeometry = THREE.ShapeGeometry;
export const SphereBufferGeometry = THREE.SphereGeometry;

export default THREE;
